import {
  getIsCalendarEmpty,
  getIsCalendarLoaded,
  getIsLeague,
  getSelectedDate,
  getSelectedDistrict,
  getSelectedLeague,
  getSelectedSport,
  getSelectedState,
  selectWidgetType,
} from 'app/imports/App.selectors'
import { UISpinner } from 'features/UI'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { WidgetTypes } from 'types'
import { getQueryParams } from 'utils'
import { DEFAULT_GAMES_COUNT_PER_SPORT, GamesGroupBySport } from '../../Games.model'
import { useGetGamesV3Query } from '../../state/Games.rtk'
import { GamesEmptyLabel } from '../GamesEmptyLabel/GamesEmptyLabel'
import { GamesGroupSport } from '../GamesGroupSport/GamesGroupSport'
import * as Styled from './GamesList.styled'

export function GamesList() {
  const widgetType = useSelector(selectWidgetType)
  const selectedState = useSelector(getSelectedState)
  const isCalendarEmpty = useSelector(getIsCalendarEmpty)
  const isCalendarLoaded = useSelector(getIsCalendarLoaded)
  const isLeague = useSelector(getIsLeague)
  const selectedLeague = useSelector(getSelectedLeague)
  const selectedDistrict = useSelector(getSelectedDistrict)
  const selectedSport = useSelector(getSelectedSport)
  const selectedDate = useSelector(getSelectedDate)

  const { allGames } = getQueryParams()

  const isAllGames = allGames === 'true'

  const skipGameByStateRequest = useMemo(() => {
    if (!selectedDate) return true

    if (widgetType === WidgetTypes.State || widgetType === WidgetTypes.StateLeague) {
      return !selectedState || selectedState.length === 0
    }

    if (widgetType === WidgetTypes.League) {
      return (
        !selectedState ||
        selectedState.length === 0 ||
        !selectedLeague ||
        selectedLeague.length === 0
      )
    }

    if (widgetType === WidgetTypes.District) {
      return (
        !selectedState ||
        selectedState.length === 0 ||
        !selectedDistrict ||
        selectedDistrict.length === 0
      )
    }

    return true
  }, [selectedDate, selectedDistrict, selectedLeague, selectedState, widgetType])

  const { data: gamesBySport, isFetching } = useGetGamesV3Query(
    {
      stateIds:
        selectedState && selectedState.length > 0
          ? selectedState.map((state) => state.id)
          : undefined,
      districtIds:
        selectedDistrict && selectedDistrict.length > 0
          ? selectedDistrict.map((district) => district.id)
          : undefined,
      leagueIds:
        selectedLeague && selectedLeague.length > 0
          ? selectedLeague.map((leagueItem) => leagueItem.id)
          : undefined,
      sportIds:
        selectedSport && selectedSport.length > 0
          ? selectedSport.map((sportItem) => sportItem.id)
          : undefined,
      isLeague,
      date: selectedDate,
      countPerSport: isAllGames ? 0 : DEFAULT_GAMES_COUNT_PER_SPORT,
    },
    { skip: skipGameByStateRequest },
  )

  const hasData = useMemo(() => {
    if (!gamesBySport) return false
    if (gamesBySport.length === 0) return false

    const groupWithGames = gamesBySport.find((group) => group.games.length > 0)

    return !!groupWithGames
  }, [gamesBySport])

  if (isCalendarEmpty && isCalendarLoaded) {
    return <GamesEmptyLabel />
  }

  if (isFetching || !gamesBySport) {
    return (
      <div style={{ marginTop: '6px' }}>
        <UISpinner />
      </div>
    )
  }

  if (!hasData) {
    return <GamesEmptyLabel />
  }

  return (
    <Styled.GroupsList>
      {gamesBySport.map((group: GamesGroupBySport) => {
        return <GamesGroupSport key={`${group.sportId}`} group={group} />
      })}
    </Styled.GroupsList>
  )
}
